import { motion } from "./motion";
import { Sparkles, ArrowRight, Lock, Brain } from "lucide-react";
import { CTA, GlowOrb, ChatBubble, PhoneFrame, EASE } from "./ui";
import { getAppUrl } from "@/urls";

// Batch 139 — the hero card. This was a hand-drawn "Best move" box that looked
// roughly like the app; it is now the app's own two-card format, trimmed.
//
// TRIMMED ON PURPOSE. 64% of mobile visitors never leave the first viewport and
// average seven seconds there, so the reasoning is left off here and the full
// three cards live below the fold in the Coaching section.
//
// NO STOP LABEL. This card carries a reply, and the app reserves STOP for a
// state that has none. Wearing the label here would show a screen the app
// never produces, which is the defect this batch exists to remove.
const PhoneScreen = () => (
  <div className="flex flex-col h-full pt-9 pb-3 px-3" data-testid="hero-coaching-card">
    <div className="flex items-center gap-2 px-1 pb-3 border-b border-[#2A2A35]">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C9A84C] to-[#8a6f2a]" />
      <div className="flex-1">
        <p className="text-[#F0F0F0] text-xs font-semibold leading-tight">Maya</p>
        <p className="text-[#7A7A8C] text-[10px] leading-tight">active now</p>
      </div>
      <Sparkles size={14} className="text-[#C9A84C]" />
    </div>

    <div className="flex-1 flex flex-col gap-2 py-3 overflow-hidden">
      <ChatBubble from="them" text="you're actually kind of funny lol" />
      <ChatBubble from="you" text="careful, low bar like that and I'll start showing off" />
      <ChatBubble from="them" text="haha this was fun. i'm gonna crash though, early start" />
    </div>

    <div className="rounded-2xl bg-[#1A1A1F] border border-[#C9A84C]/50 p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Brain size={11} className="text-[#C9A84C]" />
        <span className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-wider">Next Best Move</span>
      </div>
      <p className="text-[#F0F0F0] text-[13px] font-semibold leading-snug">
        she's not there, so don't chase it. one clean line and let her come to you.
      </p>
      <div className="mt-2.5 pt-2.5 border-t border-[#2A2A35]">
        <span className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-wider">Best Move</span>
        <div className="mt-1.5 rounded-lg border border-[#C9A84C]/20 bg-[#C9A84C]/[0.08] px-2.5 py-2">
          <p className="text-[#F0F0F0] text-[13px] leading-snug">"fair enough. good talking to you"</p>
        </div>
      </div>
    </div>
  </div>
);

export const Hero = () => (
  <section className="relative overflow-hidden pt-32 pb-20 md:pt-44 md:pb-28">
    <GlowOrb className="w-[520px] h-[520px] -top-40 -left-32 opacity-70" />
    <GlowOrb className="w-[440px] h-[440px] top-20 -right-24 opacity-50" color="rgba(201,168,76,0.12)" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div className="text-center lg:text-left">
        <motion.div
          eager
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
          className="inline-flex items-center gap-2 rounded-full border border-[#2A2A35] bg-white/[0.03] px-4 py-1.5 mb-7"
        >
          <span className="w-2 h-2 rounded-full bg-[#C9A84C] animate-pulse" />
          {/* Batch 121 — was "Private beta - limited spots". The gate now offers open
              signup, and marketing and the app are one product to anyone who sees
              both, so a scarcity claim here would contradict the door. */}
          <span className="text-[#F0F0F0]/80 text-xs font-medium tracking-wide">Free to start, no invite needed</span>
        </motion.div>

        <motion.h1
          eager
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05, ease: EASE }}
          className="font-['Bebas_Neue'] text-[#F0F0F0] text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.92] tracking-wide"
        >
          A coach that works with you,{" "}<br />not an app that{" "}
          <span className="text-[#C9A84C]">writes for you.</span>
        </motion.h1>

        <motion.p
          eager
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="mt-6 max-w-xl mx-auto lg:mx-0 font-['Inter'] text-lg md:text-xl font-light leading-relaxed text-[#A8A8B8]"
        >
          SparkCue reads the whole conversation, tells you the move and why, gives you the words.{" "}
          <span className="text-[#F0F0F0]">Sometimes the move is nothing.</span>
        </motion.p>

        <motion.div
          eager
          initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
          className="mt-9 flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start"
        >
          {/* Batch 136 — the ghost control beside this one also said "Start
              free" and scrolled to a form collecting addresses for a list that
              had nothing to give. Two controls, one word apart, one job. */}
          <CTA href={getAppUrl()} testid="hero-start-free">Start Free <ArrowRight size={18} /></CTA>
        </motion.div>

        {/* Batch 135 — the age requirement is stated HERE, under the two calls
            to action, because this is the click-through the app is entered
            through and the requirement was previously visible only to someone
            who opened the terms document. It is one more segment on a strip
            that was already middot separated, so no element is added and
            nothing moves. The testid exists so a test can read the line
            without matching on prose that is free to change around it. */}
        <motion.p
          eager
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-4 flex items-center gap-1.5 text-[#7A7A8C] text-xs justify-center lg:justify-start"
          data-testid="hero-trust"
        >
          <Lock size={12} /> Must be 18+ · No sign-up needed to try · Your chats stay on your device
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40, rotate: -4 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 1, delay: 0.2, ease: EASE }}
        className="relative"
      >
        <motion.div
          animate={{ y: [-10, 10, -10], rotate: [-1.5, 1.5, -1.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformPerspective: 1000 }}
        >
          <PhoneFrame>
            <PhoneScreen />
          </PhoneFrame>
        </motion.div>
      </motion.div>
    </div>
  </section>
);
